import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ListComponentCategoriesRequest } from "../../../../../services/soap/item/requests/ListComponentCategoriesRequest";
import { ComponentCategory } from "../../../../../services/soap/item/responses/ListComponentCategoriesResponse";

interface IItemCategoryDropDownProps {
  selectedCategory: string;
  onChange: (data: ComponentCategory | null) => void; // ✅ allow null on clear
  isEdit: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
  isCategoryRequired?: boolean;
  setIsCategoryRequired: (value: boolean) => void;
  hideLabel?: boolean;
  width?: string;
}

const ItemCategoryDropDown: React.FC<IItemCategoryDropDownProps> = ({
  selectedCategory,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
  setIsCategoryRequired,
  hideLabel,
  width,
}) => {
  const { store, itemService } = useDependency();
  const { t } = useTranslation("products");
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: "products" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<ComponentCategory>({
    async load({ filterText }) {
      try {
        const request = new ListComponentCategoriesRequest(
          store.Server,
          store.SessionId,
          {}
        );
        const response = await itemService.listComponentCategories(request);

        let items: ComponentCategory[] = response?.COMPONENT_CATEGORY || [];
        if (!Array.isArray(items)) return { items: [] };

        setIsCategoryRequired(items.length > 0);

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((c) => (c.name ?? "").toLowerCase().includes(ft));
        }

        return { items };
      } catch (e) {
        console.error("Error fetching ItemCategories:", e);
        setIsCategoryRequired(false);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE (only if not edit)
  useEffect(() => {
    if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      setSelectedKey(String((first as any).id));
      setInputValue(first.name ?? "");
      onChange(first);
    }
  }, [list.items, isEdit, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedCategory) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedCategory));
    const selected = list.items.find((x) => String((x as any).id) === String(selectedCategory));
    setInputValue(selected?.name ?? "");
  }, [selectedCategory, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    if (selectedKey) {
      const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) setSelectedKey(null);
    }

    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String((x as any).id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={hideLabel ? "" : _t("category")}
      aria-label={_t("category")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={width && width !== "" ? width : "100%"}
      isRequired={list.items.length > 0 ? isRequired : false}
      errorMessage={list.items.length > 0 ? errorMessage : undefined}
      validationState={list.items.length > 0 ? validationState : undefined}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={(item as any).id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ItemCategoryDropDown;
